
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";

// ✅ thunks
import { updateUser, fetchUserById } from "../redux/thunks/userThunks";
import {
  updateMedicalInfo,
  fetchMedicalInfoByUserId,
} from "../redux/thunks/medicalThunks";

// ✅ helpers
import formatDate from "../functions/profile/formatDate";
import getAvatarUrl from "../functions/profile/getAvatarUrl";
import mapUserUpdatePayload from "../functions/profile/mapUserUpdatePayload";
import mapMedicalUpdatePayload from "../functions/profile/mapMedicalUpdatePayload";

const pickUserId = (value) => {
  if (!value) return null;
  if (typeof value === "string" || typeof value === "number") {
    const normalized = String(value).trim();
    return normalized || null;
  }
  if (typeof value !== "object") return null;

  return (
    pickUserId(value.id) ||
    pickUserId(value._id) ||
    pickUserId(value.userId) ||
    pickUserId(value.sub) ||
    pickUserId(value.pgId) ||
    pickUserId(value.uuid) ||
    pickUserId(value.subprofileId) ||
    pickUserId(value.subProfileId) ||
    pickUserId(value.profileId)
  );
};

const findUserById = (map, userId) => {
  if (!map || !userId) return null;
  const key = String(userId);

  return (
    map[userId] ||
    map[key] ||
    Object.values(map).find((item) => String(pickUserId(item)) === key) ||
    null
  );
};

export function useUserInformation(user, medical) {
  const dispatch = useDispatch();

  // ✅ userId comes from the props only — never from the auth slice.
  //
  // The parent component always dispatches fetchUserById(targetId) and then
  // selects the result from state.user.users[targetId], passing it as `user`.
  // This means:
  //   - Own profile  → parent passes state.user.users[myId]      → user.id = myId
  //   - Doctor view  → parent passes state.user.users[patientId] → user.id = patientId
  //
  // Using auth slice here would break the doctor view with a 401 because the
  // doctor's token is scoped to their own ID, not the patient's.
  const userId = pickUserId(user) ?? pickUserId(medical) ?? null;

  // Read the latest version of this user from the normalized users map,
  // so we always have fresh data after a fetchUserById refetch.
  const storedUser = useSelector((s) =>
    userId
      ? findUserById(s.user?.users, userId) ??
        findUserById(s.user?.usersById, userId)
      : null
  );

  const freshUser = useMemo(
    () => {
      if (!storedUser) {
        return userId && user && !user.id ? { ...user, id: userId } : user;
      }

      const merged = {
        ...(user || {}),
        ...storedUser,
      };

      return merged.id ? merged : { ...merged, id: pickUserId(merged) ?? userId };
    },
    [storedUser, user, userId]
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const avatarSrc = useMemo(
    () => getAvatarUrl(freshUser, medical),
    [freshUser, medical]
  );

  /** ✅ Save — always targets the profile being viewed, never the authed user */
  const handleSubmit = useCallback(
    async (formValues) => {
      if (!userId) {
        message.error("User ID is missing.");
        return false;
      }

      try {
        setIsSaving(true);

        const userPayload = mapUserUpdatePayload(formValues, freshUser);
        const medicalPayload = mapMedicalUpdatePayload(formValues, medical);

        await dispatch(updateUser({ userId, values: userPayload })).unwrap();
        await dispatch(updateMedicalInfo({ userId, values: medicalPayload })).unwrap();

        // Refetch to sync state.user.users[userId] and state.medical.data
        await Promise.all([
          dispatch(fetchUserById(userId)),
          dispatch(fetchMedicalInfoByUserId(userId)),
        ]);

        message.success("✅ Information updated successfully!");
        return true;
      } catch (err) {
        message.error(err?.message || "An error occurred.");
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [dispatch, userId, freshUser, medical]
  );

  return {
    isModalOpen,
    openModal,
    closeModal,
    formatDate,
    avatarSrc,
    handleSubmit,
    isSaving,
  };
}

export default useUserInformation;
